<template>
  <div :class="isMaintenanceAlert ? 'card-maintenance' : ''" class="grid-container wrapper padding-xs">
    <StepUpAuthenticationOverlay v-if="persistentLoginObj.showAuthenticationOverlay" :params="paramsObj" />
    <template v-if="card.creditSummary">
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
      <div
        class="card-details grid-x align-justify margin-bottom-xs"
        tabindex="0"
        @click.prevent="selectedCard(card)"
        @keyup.enter.prevent="selectedCard(card)"
      >
        <div class="grid-y align-justify">
          <div class="wallet"></div>
          <p class="medium" data-fs-mask>{{ CREDIT_CARD.paymentCard.endingIn }} {{ card.creditSummary[0].last4digits }}</p>
        </div>
        <div class="cell small-4">
          <picture>
            <source :srcset="cardImgAvif" type="image/avif" />
            <source :srcset="cardImgWebp" type="image/webp" />
            <img
              :src="cardImg"
              :fetchpriority="props.index === 0 ? 'high' : 'low'"
              :loading="props.index === 0 ? 'eager' : 'lazy'"
              :alt="card.creditSummary[0].cardName"
            />
          </picture>
        </div>
      </div>
      <div class="margin-bottom-xs">
        <p class="small" data-fs-mask>{{ CREDIT_CARD.paymentCard.minimumPayment }} {{ card.creditSummary[0].minPaymentDueFormatted }}</p>
        <p class="small" data-fs-mask>{{ CREDIT_CARD.paymentCard.dueDate }} {{ card.creditSummary[0].paymentDueDateFormatted }}</p>
        <p
          v-if="
            getScheduledPaymentsSettings(card.creditSummary[0].last4digits) &&
            getScheduledPaymentsSettings(card.creditSummary[0].last4digits).scheduledPaymentDate
          "
          class="small"
          data-fs-mask
        >
          {{ CREDIT_CARD.paymentCard.nextAutoPayment }} {{ getScheduledPaymentsSettings(card.creditSummary[0].last4digits).scheduledPaymentDate }}
        </p>
      </div>
      <div class="grid-x margin-bottom-xs align-justify">
        <div>
          <p class="small" data-fs-mask>
            {{ CREDIT_CARD.paymentCard.currentBalance }}
            {{ card.creditSummary[0].currAcctBalanceFormatted }}
          </p>
          <p class="small" data-fs-mask>
            {{ CREDIT_CARD.paymentCard.availableCredit }}
            {{ card.creditSummary[0].availableCreditFormatted }}
          </p>
        </div>
        <div class="align-self-bottom">
          <template v-if="getReasonCode(card.creditSummary[0].last4digits) === 'A' || getPaymentEligibility(card.creditSummary[0].last4digits)">
            <Button :variant="'link'" class="link-sm" @click.prevent="makePayment()">
              {{ CREDIT_CARD.paymentCard.makeAPaymentLabel }}
            </Button>
          </template>
          <template v-else>
            <div>
              <CitiRedirectLink
                :card-id="card.creditSummary[0].cardId"
                :css="['link-sm']"
                :action="'makepaymentoptions'"
                :persistent-login-obj="persistentLoginObj"
                @calling-step-up-overlay="callFromCitiRedirectComp"
              >
                <template #citi-link-content>
                  {{ CREDIT_CARD.paymentCard.makeAPaymentLabel }}
                </template>
              </CitiRedirectLink>
            </div>
          </template>
        </div>
      </div>
      <div class="grid-x align-justify">
        <div>
          <Button :variant="'link'" class="link-sm" @click.prevent="checkLegalNotice()">
            {{ CREDIT_CARD.paymentCard.importantNoticeLabel }}
          </Button>
        </div>
        <div>
          <Button :variant="'link'" class="link-sm" @click.prevent="selectedCard(card)">
            {{ CREDIT_CARD.paymentCard.viewDetailsLabel }}
          </Button>
        </div>
      </div>
      <template v-if="isMaintenanceAlert">
        <Notification class="margin-top-xs" :severity="'info'" :closable="false">
          <Button :variant="'link'" class="notification-text link-body" @click.prevent="openMaintenanceAlert()">
            {{ creditError.notification.title }}
          </Button>
        </Notification>
      </template>
    </template>
  </div>
</template>

<script setup>
import { reactive, computed, onMounted } from 'vue';
import Button from '@atomic-ui/button/Button';
import Notification from '@atomic-ui/notification/Notification';
import { fireTag } from '@vue-util/tagManager';
import StepUpAuthenticationOverlay from '@nuxt-common/login-component/app/components/StepUpSignInOverlayWrapper';
import { WALLET, CREDIT_CARD } from 'config';
import { useProfileStore } from '@/stores/useProfileStore';
import { useWalletStore } from '@/stores/useWalletStore';
import { useEnvPropsStore } from '@/stores/useEnvPropsStore';
import { useDashboardStore } from '@/stores/useDashboardStore';
import useModal from '@/composables/useModal';
import useSlideout from '@/composables/useSlideout';
import populateRememberMe from '@/utils/populateRememberMe';
import CitiRedirectLink from '../citiRedirect/CitiRedirectLink.vue';

const props = defineProps({
  card: {
    type: Object,
    default: () => ({}),
  },
  index: {
    type: Number,
    default: 0,
  },
});

const profileStore = useProfileStore();
const walletStore = useWalletStore();
const envPropsStore = useEnvPropsStore();
const dashboardStore = useDashboardStore();
const { showModal } = useModal();
const { showSlideout } = useSlideout();

const persistentLoginObj = reactive({
  persistentLoginEnabled: false,
  showAuthenticationOverlay: false,
});

const closeStepUpOverlay = () => {
  persistentLoginObj.showAuthenticationOverlay = false;
};

const paramsObj = reactive({
  closeStepUpOverlay,
  callbackFunction: null,
});

const profile = computed(() => profileStore.profile);
const creditError = computed(() => walletStore.creditError);
const extendToken = computed(() => walletStore.extendToken);
const { killswitches } = storeToRefs(dashboardStore);
const nuxtAppCDNUrl = computed(() => envPropsStore.nuxtAppCDNUrl);

const getPaymentEligibility = computed(() => walletStore.getPaymentEligibility);
const getReasonCode = computed(() => walletStore.getReasonCode);
const getScheduledPaymentsSettings = computed(() => walletStore.getScheduledPaymentsSettings);

const isMaintenanceAlert = computed(() => CREDIT_CARD.maintenanceAlertTitle === creditError.value.notification.title);
const cardImg = computed(() => {
  const imageName = WALLET.cardImages[props.card.creditSummary[0].cardType]?.image;
  return imageName ? `${nuxtAppCDNUrl.value}/img/${imageName}` : '';
});
const cardImgWebp = computed(() => {
  return cardImg.value ? cardImg.value.replace(/\.png$/, '.webp').replace(/\.jpg$/, '.webp') : '';
});
const cardImgAvif = computed(() => {
  return cardImg.value ? cardImg.value.replace(/\.png$/, '.avif').replace(/\.jpg$/, '.avif') : '';
});

onMounted(() => {
  const { persistentLoginEnabled, persistentLoginAccountDashboardEnabled } = killswitches.value || {};
  persistentLoginObj.persistentLoginEnabled = persistentLoginEnabled && persistentLoginAccountDashboardEnabled;
});

const callFromCitiRedirectComp = (callbackFn) => {
  paramsObj.callbackFunction = callbackFn;
  persistentLoginObj.showAuthenticationOverlay = true;
};

const makePayment = (callbackFunction) => {
  if (!extendToken.value) {
    fireTag('link', {
      ...CREDIT_CARD.paymentCard.tag.makePaymentLink,
    });
  }
  if (persistentLoginObj.persistentLoginEnabled && extendToken.value && !callbackFunction) {
    paramsObj.callbackFunction = makePayment;
    persistentLoginObj.showAuthenticationOverlay = true;
    fireTag('view', {
      ...CREDIT_CARD.paymentCard.tag.makePaymentView,
    });
    return;
  }
  if (callbackFunction) {
    walletStore.setExtendToken(false);
  }
  if (getReasonCode.value(props.card.creditSummary[0].last4digits) === 'A') {
    const attentionOverlayComponent = import('./AttentionOverlay.vue');
    showModal('attentionOverlay', 'Attention!', attentionOverlayComponent, '400px');
  } else if (getPaymentEligibility.value(props.card.creditSummary[0].last4digits)) {
    window.location.href = `/my-credit/gateway/cards/${props.index}?makePayment=true&linklocation=pagecontent`;
  }
};

const checkLegalNotice = () => {
  const component = import('./ImportantNotice');
  showSlideout('important-notice', CREDIT_CARD.paymentCard.importantNoticeLabel, component);
  fireTag('link', {
    ...CREDIT_CARD.paymentCard.tag.legalNotice,
  });
};

const selectedCard = async (card, callbackFunction) => {
  const component = import('./CreditCardSlideout');
  if (persistentLoginObj.persistentLoginEnabled && extendToken.value && !callbackFunction) {
    paramsObj.callbackFunction = selectedCard.bind(this, card);
    populateRememberMe(profile.value?.email);
    persistentLoginObj.showAuthenticationOverlay = true;
    fireTag('view', {
      ...CREDIT_CARD.paymentCard.tag.selectedCardPersistentLogin,
    });
    return;
  }
  if (callbackFunction) {
    walletStore.setExtendToken(false);
  }
  const { index } = props;
  const { isClassicSunsetCreditGatewayTabEnabled, classicSunsetCreditGatewayTabEnabled } = killswitches.value || {};
  const isClassicSunsetEnabled = isClassicSunsetCreditGatewayTabEnabled || classicSunsetCreditGatewayTabEnabled || false;
  showSlideout('CreditCard-SlideOut', CREDIT_CARD.heading, component, true, { card, index, isClassicSunsetEnabled });
  fireTag('link', {
    ...CREDIT_CARD.paymentCard.tag.selectedCard,
  });
};

const openMaintenanceAlert = async () => {
  const component = import('./CreditCardSlideout');
  showSlideout('maintenance-slideout', '', component, true, { notificationContent: creditError.value.notification });
  fireTag('link', {
    ...CREDIT_CARD.paymentCard.tag.maintenanceAlert,
  });
};
</script>

<style lang="scss" scoped>
.notification-text {
  text-decoration: none;
}
.wrapper {
  border: $gray-3-border;
  box-sizing: border-box;
}
</style>

<style lang="scss" brand="mcom" scoped>
@import '@csgIcons/_wallet.scss';
.wrapper {
  border-radius: 5px;
}
</style>

<style lang="scss" brand="bcom" scoped>
@import '@csgIcons/_bwallet.scss';
.wallet {
  @extend .bwallet;
}
</style>
